<template>
  <div class="w-full px-6 py-4">
    <o-table
      class="w-full"
      container-class="border rounded bg-white dark:bg-gray-900 dark:border-gray-800 overflow-hidden"
      :data="associations"
      :headers="[
        {text: $t('service.addOn')},
        {text: $t('general.discount')},
        {text: $t('general.actions')},
      ]"
      :limit="20"
      :title="$t('components.service.edit.addOns.title')"
      striped
    >
      <template #controls>
        <o-button variant="primary" @click="handleAddServiceAddOn">
          {{ $t('components.service.edit.addOns.add') }}
        </o-button>
      </template>

      <template #row="{ row, tdClass }">
        <td :class="tdClass">
          <div class="flex items-center space-x-2">
            <p>{{ row.target?.title }}</p>

            <OTooltip v-if="!row.target?.allowInternet" :text="$t('service.addOnNotOnline')">
              <OChip variant="danger" size="sm" flat>
                {{ $t('general.notOnline') }}
              </OChip>
            </OTooltip>
          </div>
        </td>
        <td :class="tdClass">
          {{ formatDiscountAmount(row) }}
        </td>
        <td :class="tdClass">
          <div class="flex items-center space-x-1">
            <o-tooltip :text="$t('general.edit')">
              <o-button icon="edit" size="sm" @click="handleEditServiceAddOn(row)" />
            </o-tooltip>
            <o-tooltip :text="$t('general.delete')">
              <o-button icon="delete" variant="danger" size="sm" flat @click="handleRemoveServiceAddOn(row)" />
            </o-tooltip>
          </div>
        </td>
      </template>
    </o-table>

    <EditServiceAddOnModal
      ref="editServiceAddOnModal"
      :portal="panel?.portal"
      :service="_service"
      @success="handleSuccess"
    />

    <RemoveServiceAddOnModal
      ref="removeServiceAddOnModal"
      :service="_service"
      @success="handleSuccess"
    />
  </div>
</template>

<script setup lang="ts">
import EditServiceAddOnModal from '@/components/service/actions/EditAddOn.vue'
import RemoveServiceAddOnModal from '@/components/service/actions/RemoveAddOn.vue'
import { OTable, OChip } from '@/components/elements'

import { ServiceAssociationDiscountType } from '~/_api/models/serviceAssociation'
import type { Service } from '~/_api/models/service'
import type { ServiceAssociation } from '~/_api/models/serviceAssociation'

const props = defineProps<{
  service: Service
  associations: ServiceAssociation[]
}>()

const emit = defineEmits<{
  (e: 'update:service', service: Service): void
  (e: 'update'): void
}>()

const panel = inject<any>('panel')
const { $auth } = useNuxtApp()

// Template refs
const editServiceAddOnModal = ref()
const removeServiceAddOnModal = ref()

const _service = computed({
  get: () => props.service,
  set: (value) => emit('update:service', value),
})

const formatDiscountAmount = (serviceAddOn: ServiceAssociation): string => {
  if (serviceAddOn.discountType === ServiceAssociationDiscountType.Percentage) {
    return Number(serviceAddOn.discountAmount).toFixed(2) + '%'
  } else {
    return $auth.info.location.currencySymbol + Number(serviceAddOn.discountAmount).toFixed(2)
  }
}

const handleAddServiceAddOn = (): void => {
  editServiceAddOnModal.value?.open()
}

const handleEditServiceAddOn = (serviceAddOn: ServiceAssociation): void => {
  editServiceAddOnModal.value?.open(serviceAddOn)
}

const handleRemoveServiceAddOn = (serviceAddOn: ServiceAssociation): void => {
  removeServiceAddOnModal.value?.open(serviceAddOn)
}

const handleSuccess = (): void => {
  emit('update')
}
</script>
